import FootballSerializer from "../../FootballSerializer";
import { EventStatusCode } from "../../../../sports/common/event-status";
import FootballStringifier from "../../../stringifiers/football-stringifier";
import {
    durationToSeconds,
    formatDateInTimeZone,
    formatDateTime,
    formatMilliseconds,
    formatTimeInTimeZone
} from "../../../common/date-util";
import { getYardsToFirstDown } from "../../../../sports/football/utils/context-utils";
import PersonnelRole from "../../../../sports/football/enums/personnel-role";
import { getSafeOrDefault } from "../../../common/object-util";
import { sanitizeInvalidCharacters } from "../../../common/string-util";

export const EventInfoSerializer = {
    serialize(event, context) {
        const {
            fieldPosition,
            down,
            homeTeamHasPossession,
            gameConfig,
            firstQtrKickoffTeamId,
            homeTeamId,
            currentPeriod,
            gameClock,
            finalizeSettings,
            ruleSetId
        } = context;
        const { attendance, endTime } = finalizeSettings || {};
        const { general, scoring, spots } = gameConfig;
        const formattedFieldPosition = FootballSerializer._formatFieldPosition(
            gameConfig.general.fieldLength,
            fieldPosition
        );

        const {
            eventType,
            startDateTime,
            id,
            eventId,
            seasonId,
            sportId,
            statusCode,
            city: rawCity,
            stateCountry: rawStateCountry,
            venue: rawVenue,
            personnel
        } = event;
        const city = sanitizeInvalidCharacters(rawCity);
        const stateCountry = sanitizeInvalidCharacters(rawStateCountry);
        const venue = sanitizeInvalidCharacters(rawVenue);
        const {
            isConference,
            isDivision,
            isNeutral,
            isPostSeason,
            isRegional,
            isOverall
        } = eventType;

        const clockMs =
            typeof gameClock.now === "function"
                ? gameClock.now()
                : gameClock.now;
        const eventMMSS = FootballStringifier.milisecondsToMMSS(clockMs);

        // A completed game has no current period/clock; undefined fields are
        // dropped from the JSON payload so legacy never writes them
        const isFinal = statusCode >= EventStatusCode.FINAL;

        const location = `${venue ? venue + ", " : ""}${[city, stateCountry]
            .join(" ")
            .trim()}`;

        const duration = this._calculateDuration(
            startDateTime,
            endTime,
            statusCode
        );
        const timeEnd =
            endTime && statusCode >= EventStatusCode.FINAL
                ? this._localTimeDisplay(endTime)
                : "";

        const getOfficialsByRole = (role) =>
            getSafeOrDefault(
                () =>
                    personnel
                        .filter((p) => p.role === role)
                        .map((p) => sanitizeInvalidCharacters(p.fullName))
                        .join(", "),
                ""
            );

        return {
            altOff: {}, // TODO
            arenaData: false, // TODO
            attendance: attendance,
            cflData: false, // TODO
            comments: {}, // TODO
            confDivision: isDivision,
            conference: isConference,
            contextDown: down,
            contextGoalToGo: false, // TODO
            contextHasBall: homeTeamHasPossession ? "H" : "V",
            contextSpot: formattedFieldPosition,
            contextToGo: getYardsToFirstDown(context),
            currentMinute: isFinal
                ? undefined
                : parseInt(eventMMSS.split(":")[0], 10), // Same as statusMinutes?
            currentSecond: isFinal
                ? undefined
                : parseInt(eventMMSS.split(":")[1], 10), // Same as statusSeconds?
            date: formatDateInTimeZone(startDateTime),
            defPat: scoring.defensivePAT,
            downs: general.downs,
            endZoneLength: general.endZoneLength,
            drives: [], // TODO
            duration,
            exhibition: !isOverall,
            fb_context_init: true, // TODO - This might be context.gameInitialized?
            fgaDrvPlay: true, // TODO
            fieldGoal: scoring.fieldGoal,
            fieldLength: general.fieldLength,
            firstDownYards: general.firstDownYards,
            firstDownYardSet: general.firstDownYardSet || [],
            firstDownFlagStyle: general.firstDownFlagStyle,
            formationsPlayByPlay: true, // TODO
            fumbNCAA: true, // TODO
            gameName: {}, // TODO
            gameNo: {}, // TODO
            gamePeriods: currentPeriod,
            gameTk: {}, // TODO
            goals: [], // TODO
            has_xml_stats: false, // TODO
            id: eventId,
            isInShootout: false, // TODO
            is_downloaded: true, // TODO
            is_sync: false, // TODO
            kickPat: scoring.kickPAT,
            kickoffSpot: spots.kickoff,
            lastUpdate: "11/03/2022+12:09:08", // TODO
            lastUpdateUser: "serge@prestosports.com", // TODO
            last_update_timestamp: 1640006797000, // TODO
            location,
            minutesPrd: general.minutesByPeriod,
            minutesPrdOt: 0, // TODO - I think we currently use the same general.minutesByPeriod for OT?
            neutral: isNeutral,
            night: false, // TODO
            notes: {}, // TODO - This looks like it comes from Finalize Event OR from Event object but the object stores this value as a string!
            otherPat: scoring.runPAT,
            otherTouchBack: spots.touchbackYardline,
            passType: false, // TODO
            patTrySpot: spots.PATTry || spots.spotsPatScoreOption1, // Flag uses spotsPatScoreOptionX, when would 2 or 3 be used?
            penDrvPlay: false, // TODO
            penaltyTk: {}, // TODO
            playSummary: true, // TODO
            postseason: isPostSeason,
            primaryAssistTackle: false, // TODO
            primeTime: false, // TODO
            psId: eventId,
            readyToStat: true, // TODO
            referees: [
                getOfficialsByRole(PersonnelRole.Referee.code),
                getOfficialsByRole(PersonnelRole.Umpire.code),
                "", // TODO: Head Linesman
                getOfficialsByRole(PersonnelRole.LineJudge.code),
                getOfficialsByRole(PersonnelRole.BackJudge.code),
                getOfficialsByRole(PersonnelRole.FieldJudge.code),
                getOfficialsByRole(PersonnelRole.SideJudge.code),
                getOfficialsByRole(PersonnelRole.OfficialScorer.code),
                "" // TODO: Center Judge
            ],
            region: isRegional,
            rouge: 1, // TODO
            rulesPeriods: 4, // TODO
            rushType: false, // TODO
            sack1_2: true, // TODO
            sackAsTackle: true, // TODO
            sackRush: true, // TODO
            deuce: scoring.deuce,
            dropKickFieldGoal: scoring.dropKickFieldGoal,
            dropKickPAT: scoring.dropKickPAT,
            rulesetId: ruleSetId,
            safety: scoring.safety,
            safetySpot: spots.safetyKO,
            scenery: "", // TODO
            scoreBoardtime: true, // TODO
            scoreSheet: "L", // TODO
            scorer: {}, // TODO
            scoring: [], // TODO
            seasonId: seasonId,
            splitDefStat: false, // TODO
            sportCode: sportId, // Are these the same as the codes we use in app? the example was "fb" and in our app its "fball"
            startingTeam: firstQtrKickoffTeamId === homeTeamId ? "H" : "V", // This means who started the kickoff, right? Or is this who received?
            statType: "L", // TODO
            status: `${eventMMSS} ${FootballStringifier.ordinalSuffix(
                currentPeriod
            )}`,
            statusCode: Object.keys(EventStatusCode).find(
                (s) => EventStatusCode[s] === statusCode
            ),
            statusMinutes: isFinal
                ? undefined
                : parseInt(eventMMSS.split(":")[0], 10), // Same as currentMinute?
            statusPeriod: isFinal ? undefined : currentPeriod,
            statusSeconds: isFinal
                ? undefined
                : parseInt(eventMMSS.split(":")[1], 10), // Same as currentSecond?
            tackle1_2: false, // TODO
            temperature: "", // TODO
            tenthsOfSecondBack: 0, // TODO
            timeEnd,
            timeStart: formatTimeInTimeZone(startDateTime),
            toHalf: 3, // TODO
            touchBackSpot: spots.KOTouchback,
            touchDown: scoring.touchdown,
            weather: "", // TODO
            wind: "" // TODO
        };
    },

    _calculateDuration(startDateTime, endTime, statusCode) {
        if (statusCode < EventStatusCode.FINAL) return "";
        const startTimeOfDay = startDateTime
            ? formatDateTime(startDateTime, "America/New_York", "HH:mm:ss")
            : null;
        const endTimeOfDay = endTime ? this._localTimeOfDay(endTime) : null;
        let durationMs = 0;
        if (startTimeOfDay && endTimeOfDay) {
            durationMs =
                (durationToSeconds(endTimeOfDay) -
                    durationToSeconds(startTimeOfDay)) *
                1000;
            if (durationMs < 0) {
                durationMs += 24 * 60 * 60 * 1000;
            }
        }
        return formatMilliseconds(durationMs, ["hours", "minutes"]);
    },

    _localTimeOfDay(dateLike) {
        const d = new Date(dateLike);
        const hh = String(d.getHours()).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        const ss = String(d.getSeconds()).padStart(2, "0");
        return `${hh}:${mm}:${ss}`;
    },

    _localTimeDisplay(dateLike) {
        const d = new Date(dateLike);
        const minutes = String(d.getMinutes()).padStart(2, "0");
        const ampm = d.getHours() >= 12 ? "PM" : "AM";
        const hours = d.getHours() % 12 || 12;
        return `${hours}:${minutes} ${ampm}`;
    }
};
